import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Phone, Star, ArrowRight } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { useAuth } from "../lib/auth";

// iter-139 — About page, mirrors steeltooth.ca/excavation-company-simcoe/
// (image 2 & 3 from the user's Sep 3 2026 message).

const SERVICES = [
    { title: "Excavation", body: "Offering site preparation, foundation excavation, trenching, grading, and more.", img: "/images/services/excavation.jpg", slug: "excavation" },
    { title: "Demolition", body: "Delivering building demolition, deconstruction, asbestos removal, and site clean-up.", img: "/images/services/demolition.jpg", slug: "demolition" },
    { title: "Septic Services", body: "Comprehensive solutions for inspection, design, installation, and maintenance.", img: "/images/services/septic.jpg", slug: "septic" },
    { title: "Site Services", body: "Discover the ultimate solution for all your site needs with Steeltooth Contracting.", img: "/images/services/site.jpg", slug: "site" },
];

const TESTIMONIALS = [
    { name: "Doug McQuigge", body: "Corey was a great to deal with. He came when he said he would arrive and delivered his work on time. A true businessman. His attention to deal is commendable, his employees worked well with everyone. I look forward to working with Steeltooth again. Great work Steeltooth." },
    { name: "Kim Watson", body: "Steeltooth was extremely professional throughout the entire septic install. Quick, tidy, and communicative from quote to backfill." },
    { name: "Jamie R.", body: "Called about a stump removal — Corey walked the site with me, quoted the same afternoon and had the job done that week. Highly recommend." },
];

export default function AboutPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    useEffect(() => { /* iter-166 — auto-redirect removed; user can browse freely */ }, [user, navigate]);

    return (
        <MarketingShell testid="about-page">
            <section
                className="hero-dark relative h-[52vh] min-h-[380px] flex items-end text-white"
                data-testid="about-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(/images/services/excavation.jpg)" }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/50 to-slate-900/80" aria-hidden="true" />
                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pb-10">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A3FF]">Excavation Company Barrie</p>
                    <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        About Steeltooth Contracting
                    </h1>
                </div>
            </section>

            <section className="bg-slate-100 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold">Professional Excavation and Demolition Services</p>
                    <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900">Committed to customer satisfaction</h2>
                    <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
                        <p>
                            At Steeltooth Contracting, we are committed to delivering professional excavation and demolition services to our clients, ensuring that we provide the highest level of customer satisfaction. We care about saving our customers money and getting the job done on time within budget.
                        </p>
                        <p>
                            We prioritize understanding our customers' needs and ensuring they are fully satisfied with the work we complete. We don't overprice our jobs, nor do we underprice in hopes we can get "extras." Our quoting process involves attending a site meeting and using a highly detailed program that allows us to give highly accurate quotes.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold">Trusted in the Community</p>
                    <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">Simcoe County's septic + excavation experts</h2>
                    <p className="mt-4 text-slate-700 leading-relaxed">
                        Steeltooth Contracting, founded in 2021, is a locally owned and operated company in Simcoe County, providing professional septic design and installation services, commercial and residential excavation and demolition, as well as lot clearing. Our team's industry experience continues to build trust across the region.
                    </p>
                </div>
            </section>

            <section className="hero-dark bg-slate-900 text-white py-16" data-testid="about-services">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl font-bold text-center tracking-tight">
                        Comprehensive services in Southern Ontario
                    </h2>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {SERVICES.map((s) => (
                            <div
                                key={s.slug}
                                className="rounded-lg overflow-hidden bg-slate-800 border border-slate-700 hover:border-[#00A3FF] transition-colors"
                                data-testid={`about-service-${s.slug}`}
                            >
                                <div className="relative h-56 bg-slate-700">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold">{s.title}</h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.body}</p>
                                    <Link
                                        to="/contact"
                                        className="mt-4 inline-flex items-center gap-1 text-[#00A3FF] font-bold text-sm hover:text-white"
                                    >
                                        Learn more <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-2 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-[#FFB020] text-[#FFB020]" />
                        ))}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                        Trusted Excavation Company in Simcoe County
                    </h2>
                    <p className="mt-3 text-slate-600 max-w-2xl">
                        Steeltooth Contracting has built a strong reputation as a trusted excavation company in Barrie, delivering exceptional services to our clients. Don't just take our word for it — check out our reviews from satisfied clients.
                    </p>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {TESTIMONIALS.map((t) => (
                            <blockquote
                                key={t.name}
                                className="p-5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 leading-relaxed"
                                data-testid={`about-testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                            >
                                <p>"{t.body}"</p>
                                <footer className="mt-3 text-xs uppercase tracking-wider font-bold text-slate-500">{t.name}</footer>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            <section className="hero-dark bg-[#0A1128] text-white text-center py-14 px-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#00A3FF] font-bold">Ready to start?</p>
                <h2 className="mt-2 text-3xl sm:text-4xl font-bold">Talk to Steeltooth today</h2>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                    <a
                        href="tel:7053334376"
                        data-testid="about-cta-call"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#007AFF] hover:bg-[#0369A1] font-bold"
                    >
                        <Phone className="w-4 h-4" /> 705-333-4376
                    </a>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-slate-500 text-white font-bold hover:bg-white/10"
                    >
                        Get a quote <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </MarketingShell>
    );
}
